/** Quotes & invoices API calls (typed) + authenticated PDF download. */

import {
  BASE_URL,
  apiDelete,
  apiGet,
  apiPost,
  type PaginatedResponse,
  type QueryParams,
} from "@/lib/api-client";
import { getAccessToken } from "@/lib/auth-token";
import type { ApiInvoice, ApiQuote } from "./invoices.adapter";

export interface DocsListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  archived?: boolean;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const invoicesApi = {
  listQuotes: (params: DocsListParams = {}) =>
    apiGet<PaginatedResponse<ApiQuote>>("/quotes", params as QueryParams),
  createQuote: (body: Record<string, unknown>) => apiPost<ApiQuote>("/quotes", body),
  setQuoteStatus: (id: string, status: "SENT" | "ACCEPTED" | "REJECTED") =>
    apiPost<ApiQuote>(`/quotes/${id}/status/${status}`),
  convertQuote: (id: string) => apiPost<ApiInvoice>(`/quotes/${id}/convert`),
  archiveQuote: (id: string) => apiDelete<ApiQuote>(`/quotes/${id}`),
  listInvoices: (params: DocsListParams = {}) =>
    apiGet<PaginatedResponse<ApiInvoice>>("/invoices", params as QueryParams),
  createInvoice: (body: Record<string, unknown>) => apiPost<ApiInvoice>("/invoices", body),
  recordPayment: (id: string, body: Record<string, unknown>) =>
    apiPost<ApiInvoice>(`/invoices/${id}/payments`, body),
  reminder: (id: string) =>
    apiGet<{ message: string; phone: string | null; email: string | null }>(
      `/invoices/${id}/reminder`,
    ),
  archiveInvoice: (id: string) => apiDelete<ApiInvoice>(`/invoices/${id}`),
};

/** Downloads the branded invoice PDF through the authenticated API. */
export async function downloadInvoicePdf(id: string, number: string): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(`${BASE_URL}/invoices/${id}/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`PDF: HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${number}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
